import CloseRounded from "@mui/icons-material/CloseRounded";
import FullscreenExitRounded from "@mui/icons-material/FullscreenExitRounded";
import FullscreenRounded from "@mui/icons-material/FullscreenRounded";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { useMediaQuery, useTheme } from "@mui/material";
import { blue } from "@mui/material/colors";
import isFunction from "lodash/isFunction";
import isNil from "lodash/isNil";
import { useCallback, useMemo, useState } from "react";
import Flx from "../layout/Flx";
import Htag from "../typography/Htag";
import Txt from "../typography/Txt";
import ModalContent from "./components/ModalContent";

const BasicModal = ({
  show,
  onClose,
  title,
  children,
  description,
  sx,
  suppressBodyPadding,
  suppressBodyOverflow,
  bodySx,
  allowFullScreen,
  titleBottomContent,
  fullWidth = true,
  hideBackdrop,
  icon,
  maxWidth = "md",
  autoWidth,
  disableAutoMobile,
  headerSx,
  defaultFullScreen = false,
}) => {
  const theme = useTheme();
  const isMobileMediaQuery = useMediaQuery(theme.breakpoints.down("md"));
  const isMobile = isMobileMediaQuery && !disableAutoMobile;

  // Initialize fullScreen state based on defaultFullScreen prop, mobile state, or false
  const [fullScreen, setFullScreen] = useState(() => {
    if (defaultFullScreen) return true;
    return isMobile;
  });

  const _maxWidth = useMemo(() => {
    if (autoWidth) {
      return false;
    }
    return maxWidth;
  }, [maxWidth, autoWidth]);

  const _fullWidth = useMemo(() => {
    if (autoWidth) {
      return false;
    }
    return fullWidth;
  }, [autoWidth, fullWidth]);

  const modalStyles = useMemo(() => {
    let styles = {};
    if (fullScreen) {
      styles = {
        "& .MuiDialog-paper": {
          width: "100%",
          maxWidth: "100%",
          height: "100%",
          maxHeight: "100%",
          margin: 0,
          borderRadius: 0,
          transition: "0.3s",
        },
      };
      return { ...styles, ...sx };
    }
    return sx || {}; // Return sx or empty object if sx is undefined
  }, [fullScreen, sx]);

  const toggleFullscreen = useCallback(() => {
    setFullScreen((prev) => !prev);
  }, []);

  const onModalClose = useCallback(() => {
    // Reset to initial state when closing
    setFullScreen(() => {
      if (defaultFullScreen) return true;
      return isMobile;
    });
    if (isFunction(onClose)) {
      onClose(false);
    }
  }, [onClose, isMobile, defaultFullScreen]);

  return (
    <>
      <Dialog
        open={show || false}
        onClose={onModalClose}
        fullWidth={_fullWidth}
        maxWidth={_maxWidth}
        hideBackdrop={hideBackdrop}
        sx={modalStyles}
      >
        <Header
          sx={headerSx}
          title={title}
          isMobile={isMobile}
          description={description}
          onClose={onModalClose}
          titleBottomContent={titleBottomContent}
          allowFullScreen={allowFullScreen}
          onFullscreenClick={toggleFullscreen}
          fullScreen={fullScreen}
          icon={icon}
          defaultFullScreen={defaultFullScreen}
        />
        <Body
          suppressBodyPadding={suppressBodyPadding}
          suppressBodyOverflow={suppressBodyOverflow}
          sx={bodySx}
        >
          {children}
        </Body>
      </Dialog>
    </>
  );
};

const Header = ({
  title,
  description,
  onClose,
  sx,
  isMobile,
  titleBottomContent,
  allowFullScreen,
  icon,
  fullScreen,
  onFullscreenClick,
  defaultFullScreen,
}) => {
  const rootHeaderStyles = useMemo(() => {
    let sty = {
      position: "relative",
      overflow: "hidden",
      flexGrow: 0,
      flexShrink: 0,
      borderBottom: "1px solid #f2f2f2",
      padding: "16px 16px 16px 16px",
    };

    return {
      ...sty,
      ...sx,
    };
  }, [sx]);

  if (isNil(title)) {
    return null;
  }
  return (
    <>
      <ModalContent sx={rootHeaderStyles}>
        <Flx fw jb ac>
          {description ? (
            <Flx column>
              <Title isMobile={isMobile} icon={icon}>
                {title}
              </Title>
              <Txt secondary>{description}</Txt>
            </Flx>
          ) : (
            <Title isMobile={isMobile} icon={icon}>
              {title}
            </Title>
          )}
          <Tooltip title={"Close"} arrow>
            <IconButton
              color="primary"
              sx={{
                position: "absolute",
                top: "4px",
                right: "4px",
                borderRadius: "4px",
              }}
              size="small"
              onClick={() => onClose(false)}
            >
              <CloseRounded />
            </IconButton>
          </Tooltip>
        </Flx>
        {titleBottomContent}
      </ModalContent>
    </>
  );
};

const Title = ({ children, icon, isMobile }) => {
  if (icon) {
    return (
      <Flx ac gap={0.8} sx={{ color: "#232a31", pr: 4 }}>
        {icon}
        <Htag sx={{ lineHeight: 1.4 }} h2>
          {children}
        </Htag>
      </Flx>
    );
  }
  return (
    <Htag sx={{ lineHeight: 1.4, pr: 4 }} h2>
      {children}
    </Htag>
  );
};

const Body = ({ children, suppressBodyPadding, suppressBodyOverflow, sx }) => {
  const styles = useMemo(() => {
    let sty = { position: "relative" };

    if (suppressBodyPadding) {
      sty = { ...sty, padding: 0 };
    }
    if (suppressBodyOverflow) {
      sty = { ...sty, overflow: "hidden" };
    }
    return {
      ...sty,
      ...sx,
    };
  }, [sx, suppressBodyPadding, suppressBodyOverflow]);
  return (
    <>
      <ModalContent sx={styles}>{children}</ModalContent>
    </>
  );
};

const FullscreenToggleButton = ({
  allowFullScreen,
  fullScreen,
  onFullscreenClick,
}) => {
  if (!allowFullScreen) {
    return null;
  }
  if (fullScreen) {
    return (
      <IconButton
        color="primary"
        size="small"
        sx={{
          background: blue[50],
          borderRadius: "4px",
        }}
        onClick={onFullscreenClick}
      >
        <FullscreenExitRounded />
      </IconButton>
    );
  }
  return (
    <Tooltip title={"Full Screen"} arrow>
      <IconButton
        color="primary"
        size="small"
        sx={{
          borderRadius: "4px",
        }}
        onClick={onFullscreenClick}
      >
        <FullscreenRounded />
      </IconButton>
    </Tooltip>
  );
};

export default BasicModal;
